import React, { useState, useEffect } from 'react';

const navLinks = [
  { label: 'Services',     id: 'services' },
  { label: 'For Brands',   id: 'brands' },
  { label: 'For Creators', id: 'creators' },
  { label: 'Results',      id: 'results' },
  { label: 'Contact',      id: 'contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [hovered, setHovered]     = useState(null);
  const [ctaHover, setCtaHover]   = useState(false);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const isDark = !scrolled && !menuOpen;

  return (
    <nav style={{
      position: 'fixed',
      top: 0, left: 0, right: 0,
      zIndex: 1000,
      background: scrolled || menuOpen ? 'rgba(255,255,255,0.98)' : 'transparent',
      borderBottom: scrolled || menuOpen ? '1px solid var(--border)' : '1px solid transparent',
      backdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
      transition: 'all 0.4s ease',
    }}>
      <div style={{
        padding: '0 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '72px',
        maxWidth: '1280px',
        margin: '0 auto',
      }}>

        {/* Logo */}
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '17px',
          fontWeight: 800,
          letterSpacing: '0.08em',
          color: isDark ? 'var(--white)' : 'var(--ink)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div style={{
            width: '30px',
            height: '30px',
            background: 'var(--accent)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 900,
            color: '#0a0a0a',
          }}>N</div>
          Niles Ascend
        </div>

        {/* Desktop links */}
        {!isMobile && (
          <ul style={{ display: 'flex', alignItems: 'center', gap: '36px', listStyle: 'none' }}>
            {navLinks.map((l) => (
              <li key={l.id}>
                <span
                  onClick={() => scrollTo(l.id)}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: isDark
                      ? (hovered === l.id ? 'var(--accent)' : 'rgba(255,255,255,0.7)')
                      : (hovered === l.id ? 'var(--accent)' : 'var(--ink-muted)'),
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={() => setHovered(l.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {l.label}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Desktop CTA */}
        {!isMobile && (
          <button
            onClick={() => scrollTo('contact')}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '11px 28px',
              background: ctaHover ? 'var(--accent-dark)' : 'var(--accent)',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={() => setCtaHover(true)}
            onMouseLeave={() => setCtaHover(false)}
          >
            Get Started
          </button>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}
          >
            <span style={{ display: 'block', width: '22px', height: '2px', background: isDark ? 'var(--white)' : 'var(--ink)', transition: 'all 0.3s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: isDark ? 'var(--white)' : 'var(--ink)', transition: 'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: isDark ? 'var(--white)' : 'var(--ink)', transition: 'all 0.3s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
          </button>
        )}
      </div>

      {/* Mobile menu */}
      {isMobile && menuOpen && (
        <div style={{ background: 'var(--white)', borderTop: '1px solid var(--border)', padding: '16px 32px 28px' }}>
          {navLinks.map((l) => (
            <div
              key={l.id}
              onClick={() => scrollTo(l.id)}
              style={{ fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, color: 'var(--ink)', padding: '14px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {l.label}
            </div>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            style={{ marginTop: '20px', width: '100%', padding: '14px', background: 'var(--accent)', color: '#0a0a0a', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Get Started
          </button>
        </div>
      )}
    </nav>
  );
}
